import { getDashboardLink, getOauthRedirectUrlWithCode, getRedirectLink } from '../utils/postInstallation';
import { useHandleBusinessFirst } from './useHandleBusinessFirst';
import { useInstallerContext } from '../context/InstallerContext';
import { getManageAppsRequiredAction, getManageAppsUrl } from '../consts';
import { useNavigate } from './useNavigate';
import { getInstalledApp } from '../utils/provision';
import { reportPostInstallation, reportRedirectUrlEnd, reportRedirectUrlStart } from '../bi/bi';
import { useModalsAndWindow } from '../context/ModalsAndWindowContext';
import { ActionType } from '@wix/ambassador-devcenter-site-apps-v1-app/types';
import { usePanorama } from '../context/PanoramaContext';
import isEmpty from 'lodash/isEmpty';
import { validatePostMessageInstallStatus } from '../utils/oauth-validations';
import { useEffect } from 'react';
export const useHandlePostInstallation = () => {
  const {
    newWindowHandlers: {
      updateWindowSrcWithUrl,
      closeWindow,
      navigateToSecondRedirectUrlInOauth,
      navigateToPostInstallationUrlInOauth,
      avoidPostInstallationPostMessageInOauth
    },
    modals
  } = useModalsAndWindow();
  const {
    panoramaLogger
  } = usePanorama();
  const {
    appId,
    overrideNavigate,
    avoidPostInstallation,
    tenantId,
    preInstallationData: {
      isExternalDashboard
    },
    version
  } = useInstallerContext();
  useEffect(() => {
    // for cleanup and not making clutter of event listeners on multiple usage of installer
    if (window.__HANDLE_OAUTH_POST_INSTALL__) {
      window.removeEventListener('message', window.__HANDLE_OAUTH_POST_INSTALL__);
      delete window.__HANDLE_OAUTH_POST_INSTALL__;
    }
  }, []);
  const navigate = useNavigate();
  const fetchBusinessFirstLink = useHandleBusinessFirst();
  const avoidPostInstallInNewWindow = () => {
    closeWindow();
  };
  const skipBusinessFirstCheck = (overrideNavigate == null ? void 0 : overrideNavigate.skipBusinessFirstCheck) || version === 'dev';
  return async (provisionSuccessData, isNewWindowOpened) => {
    var _getInstalledApp;
    const requiredActions = ((_getInstalledApp = getInstalledApp(appId, provisionSuccessData)) == null ? void 0 : _getInstalledApp.requiredActions) || [];
    const requiredActionsWithoutOauth = requiredActions.filter(requiredAction => requiredAction.actionType !== ActionType.OAUTH2);
    let businessFirstUrl = '';
    const getBusinessFirstLink = async () => {
      businessFirstUrl = (await fetchBusinessFirstLink(requiredActionsWithoutOauth)) || '';
      return businessFirstUrl;
    };
    const oauthRedirectLinkWithCode = getOauthRedirectUrlWithCode(requiredActions);
    if (oauthRedirectLinkWithCode) {
      reportRedirectUrlStart({
        url: oauthRedirectLinkWithCode
      });
      navigateToSecondRedirectUrlInOauth(oauthRedirectLinkWithCode);
      const handlePostInstallInOauth = event => {
        if (validatePostMessageInstallStatus(event)) {
          var _getRedirectLink;
          const thirdRedirectUrl = ((_getRedirectLink = getRedirectLink(requiredActionsWithoutOauth)) == null ? void 0 : _getRedirectLink.link) || getManageAppsUrl(tenantId);
          reportRedirectUrlEnd({
            url: avoidPostInstallation ? 'avoid-post-installation' : thirdRedirectUrl
          });
          if (avoidPostInstallation) {
            avoidPostInstallationPostMessageInOauth();
          } else {
            navigateToPostInstallationUrlInOauth(thirdRedirectUrl);
            reportPostInstallation({
              destination: thirdRedirectUrl
            });
          }
        }
      };
      // case importify - if the app has external dashboard but still uses /close-window, keep an event listener for it
      window.addEventListener('message', handlePostInstallInOauth);
      window.__HANDLE_OAUTH_POST_INSTALL__ = handlePostInstallInOauth;
      return;
    }
    if (avoidPostInstallation) {
      avoidPostInstallInNewWindow();
      return;
    }
    if (isExternalDashboard) {
      const externalDashboardLink = getDashboardLink(requiredActions);
      if (externalDashboardLink) {
        reportPostInstallation({
          destination: externalDashboardLink
        });
        updateWindowSrcWithUrl(externalDashboardLink);
      }
      return;
    }
    if (!skipBusinessFirstCheck && (await getBusinessFirstLink())) {
      avoidPostInstallInNewWindow();
      reportPostInstallation({
        destination: businessFirstUrl
      });
      await modals.businessFirst.open({
        businessFirstUrl
      });
      return;
    }
    const postInstallRedirect = getRedirectLink(requiredActionsWithoutOauth) ?? getManageAppsRequiredAction(tenantId);
    if (isEmpty(requiredActionsWithoutOauth)) {
      panoramaLogger('No required actions to handle (requiredActionsWithoutOauth)');
    }
    if (postInstallRedirect.link) {
      reportPostInstallation({
        destination: postInstallRedirect.link
      });
      navigate(postInstallRedirect, isNewWindowOpened, updateWindowSrcWithUrl, avoidPostInstallInNewWindow, {
        appId,
        version
      });
    }
  };
};
//# sourceMappingURL=useHandlePostInstallation.js.map